import { useInfiniteQuery } from '@tanstack/react-query';
import { PagingListReturnModel } from '@cks-internal/react-base';
import { getAxios } from '../../../util/axios.ts';
import { ControllerRoutes } from '../../../constants/controllerRoutes.ts';
import { ProductListingModel } from '../productView/models/ProductListingModel.ts';

type genericCrudFilterState = {
  includeDisabled?: boolean;
  globalFilter?: string;
};

export type ProductFilterState = genericCrudFilterState & {
  formIds?: number[] | undefined;
  keyword?: string;
};

export type SortBy = 'packSizePrice' | 'name';

export type SortingState = {
  orderBy?: SortBy;
  isDesc?: boolean;
};

type ProductParams = {
  sortingState: SortingState;
  filterState?: ProductFilterState;
  pageSize?: number;
};

const defaultPageSize = 50;

const productKeys = {
  all: [{ scope: 'products' }] as const,
  lists: () => [{ ...productKeys.all[0], entity: 'list' }] as const,
  infiniteList: (sorting: SortingState, filter?: ProductFilterState) =>
    [{ ...productKeys.lists()[0], sorting, filter }] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: number) => [{ ...productKeys.details(), id }] as const
};

async function fetchProducts(
  pageParam: number,
  pageSize: number,
  sorting: SortingState,
  filter?: ProductFilterState
): Promise<PagingListReturnModel<ProductListingModel>> {
  return (
    await getAxios().get<PagingListReturnModel<ProductListingModel>>(ControllerRoutes.ProductListing.Base, {
      params: {
        page: pageParam,
        pageSize,
        ...sorting,
        ...filter
      },
      paramsSerializer: {
        indexes: null // use brackets with indexes
      }
    })
  ).data;
}

export function useProductListing(params: ProductParams) {
  const pageSize = params.pageSize ?? defaultPageSize;

  return useInfiniteQuery({
    queryKey: productKeys.infiniteList(params.sortingState, params.filterState),
    queryFn: ({ pageParam }) => fetchProducts(pageParam, pageSize, params.sortingState, params.filterState),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((acc, page) => acc + page.PagedRows.length, 0);
      if (totalLoaded >= lastPage.TotalRows) {
        return undefined;
      }
      return allPages.length;
    }
  });
}
